import SharedProcessingTimeSection from "@/components/feature/SharedProcessingTimeSection";
import type { ProcessingOption } from "@/components/feature/SharedProcessingTimeSection";

const options: ProcessingOption[] = [
  {
    speed: "24",
    unit: "Hours*",
    label: "Regional Agency Processing",
    tag: "Emergency Only",
    tagColor: "bg-gray-100 text-gray-500",
    features: [
      "Closest regional agency: New Orleans — ~200 miles via I-10",
      "NPIC appointment required before driving to New Orleans",
      "Life-or-death emergencies only",
      "Bereavement or medical crisis documentation required",
      "Expect to wait all day or return next day",
    ],
    highlight: false,
    emergency: true,
  },
  {
    speed: "2–9",
    unit: "Business Days",
    label: "Fast Passport Center",
    tag: "Most Popular",
    tagColor: "bg-[#F6C344] text-[#1A365D]",
    features: [
      "Registered U.S. Department of State Courier",
      "Reserved agency submission — no drive to New Orleans needed",
      "Ideal for NAS Pensacola military families on PCS orders",
      "Perfect for student naval aviators deploying overseas",
      "Great for University of West Florida students studying abroad",
      "Document review included",
      "Dedicated agent assigned to your case",
      "Daily status updates — never left guessing",
      "Not affected by government shutdowns",
    ],
    highlight: true,
  },
  {
    speed: "3–5",
    unit: "Weeks",
    label: "Fast Passport Center",
    tag: "Standard Expedited",
    tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
    features: [
      "Registered U.S. Department of State Courier",
      "Reserved agency submission",
      "Faster than self-service at Escambia County post offices",
      "Good for planned international trips from PNS",
      "Document review included",
      "Dedicated agent assigned to your case",
      "Daily status updates — never left guessing",
      "Not affected by government shutdowns",
    ],
    highlight: false,
    smartSaver: true,
  },
  {
    speed: "1–2",
    unit: "Months",
    label: "Self-Service (Post Office)",
    tag: "Slowest",
    tagColor: "bg-gray-100 text-gray-400",
    features: [
      "No courier access — standard mail only",
      "No document review — errors cause rejection",
      "In-person clerk appointment required at Pensacola area facilities",
      "Workload shifts with government shutdowns and budget fights",
      "No dedicated agent — you are on your own",
      "Updates only by physical mail letter",
      "No phone or email contact for your case",
      "Cannot upgrade speed after submission",
      "Not suitable if travel is within 10 weeks",
    ],
    highlight: false,
    muted: true,
  },
];

export default function PensacolaProcessingTimeSection() {
  return (
    <SharedProcessingTimeSection
      sectionId="processing-options"
      cityName="Pensacola"
      headingExtra="FL"
      introText="Pensacola and Emerald Coast travelers — NAS Pensacola military families on PCS and deployment orders, student naval aviators shipping to international training rotations, Blue Angels support crew traveling overseas, University of West Florida students studying abroad, Pensacola Beach vacationers with Caribbean cruises booked, and Pensacola International Airport (PNS) flyers — have several options for getting a passport fast. Here is how they compare, and why most Panhandle residents choose a U.S. Department of State registered passport courier for predictable, professional processing without the 3+ hour drive to New Orleans."
      options={options}
      emergencyDescription="NAS Pensacola — the 'Cradle of Naval Aviation' — trains thousands of student naval aviators, flight officers, and aircrew who deploy to international training rotations on short notice. A naval flight officer awaiting F/A-18 orders to Oceana or an exchange posting to a NATO ally cannot gamble on New Orleans's single agency. The 200-mile I-10 journey crosses the Mobile Bay bridge-tunnel complex and the Bonnet Carré Spillway — during hurricane season (June–November), contraflow evacuation orders turn I-10 into a one-way parking lot. A Blue Angel mechanic with a passport expiring 72 hours before an international airshow cannot sit in evacuation gridlock on I-10 hoping one agency in New Orleans can squeeze them in."
      disclaimerText="*Processing speed depends on eligibility and documentation. Pensacola does not have a regional passport agency — the closest is New Orleans, roughly 200 miles and 3+ hours away via I-10 through hurricane-prone Gulf Coast corridor. Not all applicants qualify for Regional Agency Processing."
    />
  );
}